import { InfoDescription } from '@/components/layout/home/info-description';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { INFO_ITEMS } from '@/utils/data/infos';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

export default function Infos() {
  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef(null),
    title: useRef(null),
    item: useRef<HTMLDivElement[]>([]),
  };

  const setItemRef = (el: HTMLDivElement | null, index: number) => {
    if (el) refs.item.current[index] = el;
  };

  useGSAP(() => {
    if (!refs.section.current || !refs.title.current) return;

    const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });

    const itemSplits = refs.item.current
      .map((item) => {
        const num = item?.querySelector<HTMLElement>('[data-num]');
        const h3 = item?.querySelector('h3');
        const p = item?.querySelector('p[aria-hidden="true"]');
        if (!num || !h3 || !p) return null;
        return {
          num,
          h3: new SplitText(h3, { mask: 'lines', type: 'lines' }),
          p: new SplitText(p, { mask: 'lines', type: 'lines' }),
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });
    gsap.set(
      itemSplits.map((s) => s.num),
      { autoAlpha: 0, x: -6 },
    );

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: refs.section.current,
        start: 'top 60%',
        toggleActions: 'play none none reverse',
      },
    });

    tl.to(refs.tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm, ease: MOTION.ease })
      .from(
        splitTitle.lines,
        {
          yPercent: MOTION.yPercent,
          stagger: MOTION.staggerLines,
          duration: MOTION.durationMd,
          ease: MOTION.ease,
        },
        '<+0.15',
      )
      .addLabel('items', '<+0.25');

    itemSplits.forEach(({ num, h3, p }, i) => {
      const start = `items+=${i * 0.12}`;
      tl.to(num, { autoAlpha: 1, x: 0, duration: MOTION.durationSm, ease: MOTION.ease }, start)
        .from(
          h3.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.05',
        )
        .from(
          p.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.1',
        );
    });

    return () => {
      splitTitle.revert();
      itemSplits.forEach(({ h3, p }) => {
        h3.revert();
        p.revert();
      });
    };
  });

  return (
    <section ref={refs.section} className="max-width-white-section">
      <div className="max-width-white-section-content container-infos flex flex-col items-start gap-10">
        <div className="flex flex-col gap-3">
          <Tag ref={refs.tag}>Questions fréquentes</Tag>
          <h2 ref={refs.title} className="w-fit text-left">
            Une réponse simple à vos questions
          </h2>
        </div>

        <div className="grid w-full grid-cols-1 gap-x-16 gap-y-0 md:grid-cols-2">
          {INFO_ITEMS.map((item, index) => (
            <div
              key={item.title}
              ref={(el) => setItemRef(el, index)}
              className="border-dark-blue/[0.07] flex flex-col gap-3 border-t py-8"
            >
              <div className="flex items-center gap-3">
                <span
                  className="text-orange/40 font-roboto-medium mt-0.5 w-6 shrink-0 text-xs tabular-nums select-none"
                  data-num
                >
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="font-roboto-medium text-midnight-blue text-base leading-snug">
                  {item.title}
                </h3>
              </div>

              <div className="pl-9">
                <InfoDescription description={item.description} highlight={item.highlight} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
